import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { wrapFunctionalComponent } from 'utils/framework';
import Modal from 'components/Modal';
import { Box, Flex, Text } from 'components/ui';
import { colors, space } from 'style/config';
import Empty from 'constants/empty';
import localeUtils from 'utils/LanguageLocale';
import SavedForLaterModalEN from 'components/GlobalModals/SavedForLaterModal/locales/SavedForLaterModal_en_US';
import SavedForLaterModalFR from 'components/GlobalModals/SavedForLaterModal/locales/SavedForLaterModal_fr_CA';
import ProductImage from 'components/Product/ProductImage';
import skuHelpers from 'utils/skuHelpers';
import ProductInfo from 'components/GlobalModals/SavedForLaterModal/ProductInfo';
import PriceDisplay from 'components/GlobalModals/SavedForLaterModal/PriceDisplay';
import ActionButton from 'components/GlobalModals/SavedForLaterModal/ActionButton';
import RemoveLink from 'components/GlobalModals/SavedForLaterModal/RemoveLink';
import ConfirmationMessage from 'components/GlobalModals/SavedForLaterModal/ConfirmationMessage';
import { withSavedForLaterModalProps } from 'viewModel/globalModals/savedForLaterModal/withSavedForLaterModalProps';
import { triggerSaveForLaterPageLoad, SFL_ACTIONS, SFL_CONTEXTS } from 'analytics/bindingMethods/pages/basket/saveForLaterBindings';

const { getLocalisedDataFromResource } = localeUtils;
const MODAL_WIDTH = 4;

function SavedForLaterModal(props) {
    const {
        isOpen,
        items = Empty.Array,
        movingSkuIds = Empty.Array,
        confirmation,
        userId,
        listId,
        handleClose,
        removeSavedForLater,
        showChangeMethodModal,
        moveItemToBasket,
        setMovingSku,
        clearConfirmation,
        undoMoveToBasket
    } = props;

    const getText = getLocalisedDataFromResource(SavedForLaterModalEN, SavedForLaterModalFR);

    useEffect(() => {
        if (isOpen) {
            triggerSaveForLaterPageLoad({ action: SFL_ACTIONS.VIEW_ALL });
        }
    }, [isOpen]);

    const handleRemove = sku => event => {
        event.preventDefault();
        event.stopPropagation();
        removeSavedForLater({ sku, userId, listId, context: SFL_CONTEXTS.MODAL });
    };

    const handleMoveToBasket = sku => () => {
        // Create callback that will be invoked when user selects delivery method
        const onChangeMethod = params => {
            const skuId = params.skuId || sku.skuId || sku.sku?.skuId;

            // Mark SKU as moving when user confirms delivery method
            if (skuId) {
                setMovingSku(skuId);
            }

            // Move item to basket
            return moveItemToBasket({
                ...params,
                userId,
                listId,
                sku
            });
        };

        showChangeMethodModal({
            sku,
            qty: 1,
            onChangeMethod,
            context: SFL_CONTEXTS.MODAL
        });
    };

    const handleDismissConfirmation = () => {
        clearConfirmation();
    };

    const handleUndoMoveToBasket = () => {
        if (confirmation) {
            undoMoveToBasket({ ...confirmation, userId, listId, context: SFL_CONTEXTS.MODAL });
        }
    };

    const handleModalClose = () => {
        clearConfirmation();
        handleClose();
    };

    const renderMobileLayout = ({ sku, isOutOfStock, isMoving, imageProps }) => {
        return (
            <Box
                display={['block', 'block', 'none']}
                css={styles.mobile.container}
            >
                <Flex
                    gap={3}
                    alignItems='flex-start'
                >
                    <Box css={styles.mobile.productImage}>
                        <ProductImage {...imageProps} />
                    </Box>

                    <Flex css={styles.mobile.productInfo}>
                        <ProductInfo sku={sku} />
                        <PriceDisplay sku={sku} />
                    </Flex>
                </Flex>

                <Box css={styles.mobile.buttonPosition}>
                    <ActionButton
                        sku={sku}
                        isOutOfStock={isOutOfStock}
                        isMoving={isMoving}
                        onClick={handleMoveToBasket(sku)}
                        getText={getText}
                    />
                </Box>

                <Box css={styles.mobile.removePosition}>
                    <RemoveLink
                        onClick={handleRemove(sku)}
                        getText={getText}
                    />
                </Box>
            </Box>
        );
    };

    const renderDesktopLayout = ({ sku, isOutOfStock, isMoving, imageProps }) => {
        return (
            <Box display={['none', 'none', 'block']}>
                <Flex
                    gap={4}
                    alignItems='flex-start'
                    justifyContent='space-between'
                >
                    <Flex
                        gap={4}
                        alignItems='flex-start'
                        flex={1}
                    >
                        <Box css={styles.desktop.productImage}>
                            <ProductImage {...imageProps} />
                        </Box>

                        <Flex css={styles.desktop.productInfo}>
                            <ProductInfo sku={sku} />
                        </Flex>
                    </Flex>

                    <Flex
                        gap={5}
                        alignItems='flex-start'
                        flexShrink={0}
                    >
                        <Box css={styles.desktop.priceColumn}>
                            <PriceDisplay sku={sku} />
                        </Box>

                        <Box flexShrink={0}>
                            <ActionButton
                                sku={sku}
                                isOutOfStock={isOutOfStock}
                                isMoving={isMoving}
                                onClick={handleMoveToBasket(sku)}
                                getText={getText}
                            />
                        </Box>
                    </Flex>
                </Flex>

                <Box css={styles.desktop.removePosition}>
                    <RemoveLink
                        onClick={handleRemove(sku)}
                        getText={getText}
                    />
                </Box>
            </Box>
        );
    };

    const renderItemRow = (item, index) => {
        const sku = item.sku || item;
        const skuId = item.sku?.skuId || item.skuId;
        const isOutOfStock = skuHelpers.isSkuOutOfStockByBasketType(sku, null, false);
        const isMoving = movingSkuIds.includes(skuId);

        const imageSize = [48, 48, 108];
        const imageProps = {
            id: skuId,
            badge: sku?.badge || null,
            altText: sku?.badgeAltText || null,
            size: imageSize,
            skuImages: sku?.skuImages
        };

        return (
            <Box
                key={`${skuId}-${index}`}
                css={styles.itemContainer}
            >
                {renderMobileLayout({ sku, isOutOfStock, isMoving, imageProps })}
                {renderDesktopLayout({ sku, isOutOfStock, isMoving, imageProps })}
            </Box>
        );
    };

    return (
        <Modal
            isOpen={isOpen}
            onDismiss={handleModalClose}
            isDrawer={false}
            width={MODAL_WIDTH}
        >
            <Modal.Header>
                <Modal.Title>{`${getText('title')} (${items.length})`}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <ConfirmationMessage
                    confirmation={confirmation}
                    onDismiss={handleDismissConfirmation}
                    onUndo={handleUndoMoveToBasket}
                    getText={getText}
                />
                {items.length === 0 ? (
                    <Box css={styles.emptyState}>
                        <Text color={colors.gray}>{getText('noItems')}</Text>
                    </Box>
                ) : (
                    items.map((item, index) => renderItemRow(item, index))
                )}
            </Modal.Body>
        </Modal>
    );
}

const styles = {
    itemContainer: {
        position: 'relative',
        padding: space[3],
        borderBottom: `1px solid ${colors.lightGray}`,
        marginY: space[3]
    },
    mobile: {
        container: {
            position: 'relative',
            minHeight: '90px'
        },
        productImage: {
            width: '48px',
            height: '48px',
            flexShrink: 0
        },
        productInfo: {
            flexDirection: 'column',
            flex: '1 1 auto',
            gap: '4px',
            paddingRight: '140px',
            marginBottom: `${space[5]}px`
        },
        buttonPosition: {
            position: 'absolute',
            top: 0,
            right: 0
        },
        removePosition: {
            position: 'absolute',
            bottom: `${space[3]}px`,
            right: 0
        }
    },
    desktop: {
        productImage: {
            width: '108px',
            height: '108px',
            flexShrink: 0
        },
        productInfo: {
            flexDirection: 'column',
            gap: '2px',
            minWidth: '233px',
            flex: '1 1 auto'
        },
        priceColumn: {
            minWidth: '120px',
            textAlign: 'right',
            flexShrink: 0
        },
        removePosition: {
            position: 'absolute',
            bottom: `${space[3]}px`,
            right: `${space[3]}px`
        }
    },
    emptyState: {
        padding: space[4],
        textAlign: 'center'
    }
};

SavedForLaterModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    items: PropTypes.array,
    movingSkuIds: PropTypes.array,
    confirmation: PropTypes.object,
    userId: PropTypes.string,
    listId: PropTypes.string,
    handleClose: PropTypes.func.isRequired,
    removeSavedForLater: PropTypes.func.isRequired,
    showChangeMethodModal: PropTypes.func.isRequired,
    moveItemToBasket: PropTypes.func.isRequired,
    setMovingSku: PropTypes.func.isRequired,
    removeMovingSku: PropTypes.func.isRequired,
    clearConfirmation: PropTypes.func.isRequired,
    undoMoveToBasket: PropTypes.func.isRequired
};

export default withSavedForLaterModalProps(wrapFunctionalComponent(SavedForLaterModal, 'SavedForLaterModal', true));
